/**
 * Rithmic credentials are EXCLUSIVELY FE-owned (mirrors the 2026-07-08 CQG directive: broker
 * credentials are FORBIDDEN from transiting our BE). Stashed in localStorage on connect; the relay
 * reads them at every socket-open to build + send the Rithmic `RequestLogin` DIRECTLY over the
 * browser's Rithmic WebSocket — the only wire they ever ride. /rithmic/connect is a credential-less,
 * body-less POST. NOTE: localStorage is XSS-readable — the same accepted trade-off as CQG (survives
 * crash/restart, prefilled reconnect); revisit with a more secure store if requirements change.
 *
 * Unlike CQG, Rithmic creds also carry the chosen `system` (gateway/system name, e.g. "Rithmic Test")
 * because the login frame needs it and the browser owns the whole logon.
 */
export const CREDS_STORAGE_KEY = 'rithmic.credentials.v1';

/**
 * Rithmic gateways / systems the user can log in to. Plain string list for now (the BE resolves the
 * wss endpoint from the chosen system). Lives here (not in the dialog) so the connect dialog AND the
 * Add-account dialog share one list. TODO(rithmic): source this from ResponseRithmicSystemInfo
 * (template 17) so the list reflects what the gateway actually offers, rather than a hardcoded set.
 */
export const RITHMIC_SYSTEMS = [
  'Rithmic Test',
  'Rithmic Paper Trading',
  'Apex',
  'Bulenox',
  'Earn2Trade',
  'TopstepTrader',
  'Rithmic 01',
  // Placeholder per Rithmic's 2026-07-18 attribution note ("Rithmic 04 Colo" + worldwide gateways
  // per system); the definitive system/gateway list arrives once attribution is complete.
  'Rithmic 04 Colo',
] as const;

/**
 * The system a connect form starts on when nothing is stored (owner ruling 2026-09-23). NOT
 * `RITHMIC_SYSTEMS[0]`: that is "Rithmic Test", the dev-kit-only system no customer can log in to —
 * prod 2026-09-20/22 a returning trader landed there, Rithmic refused (rp_code 13 "permission denied"),
 * and he had to switch to "Rithmic 01" by hand. The list order is unchanged.
 */
export const DEFAULT_RITHMIC_SYSTEM: (typeof RITHMIC_SYSTEMS)[number] = 'Rithmic 01';

/**
 * Rithmic's production **points of presence**, released to us on completing conformance (2026-07-29
 * email). Every POP serves the same systems — "the URIs support the Rithmic various systems,
 * including Rithmic Paper Trading and Rithmic 01, from various gateways/points-of-presence" — so this
 * is purely *where the socket lands*, i.e. a latency choice, never a "what can I log in to" choice.
 *
 * The `value` codes must match `RithmicOptions.Locations` in the BE (which owns the actual wss URLs,
 * so a POP can be repointed by config without an FE release). `core` is Chicago and is the BE's
 * pre-existing production gateway, so it stays the default and nothing changes for anyone who
 * ignores this dropdown. Not offered for "Rithmic Test": that system exists only on the dev-kit host.
 */
export const RITHMIC_LOCATIONS = [
  { value: 'core', label: 'Chicago (core)' },
  { value: 'nyc', label: 'New York' },
  { value: 'colo75', label: 'Chicago Colo75' },
  { value: 'ie', label: 'Ireland' },
  { value: 'de', label: 'Frankfurt' },
  { value: 'za', label: 'Cape Town' },
  { value: 'in', label: 'Mumbai' },
  { value: 'sg', label: 'Singapore' },
  { value: 'hk', label: 'Hong Kong' },
  { value: 'kr', label: 'Seoul' },
  { value: 'jp', label: 'Tokyo' },
  { value: 'au', label: 'Sydney' },
  { value: 'br', label: 'Sao Paolo' },
] as const;

export const DEFAULT_RITHMIC_LOCATION = 'core';

/** The Test system is hosted on the single dev-kit gateway, so a POP choice cannot apply to it. */
export const systemSupportsLocation = (system: string) =>
  system.trim().toLowerCase() !== 'rithmic test';

export interface RithmicCreds {
  system: string;
  username: string;
  password: string;
  /** Chosen point of presence (see `RITHMIC_LOCATIONS`); '' = the BE's core gateway. */
  location: string;
}

const EMPTY: RithmicCreds = { system: '', username: '', password: '', location: '' };

export function readStoredRithmicCreds(): RithmicCreds {
  try {
    const raw = localStorage.getItem(CREDS_STORAGE_KEY);
    if (!raw) return { ...EMPTY };
    const p = JSON.parse(raw) as Partial<RithmicCreds>;
    return {
      system: p.system ?? '',
      username: p.username ?? '',
      password: p.password ?? '',
      // Absent on creds stored before 2026-07-29 — '' resolves to the core gateway BE-side, which
      // is exactly where those sessions were already going.
      location: p.location ?? '',
    };
  } catch { return { ...EMPTY }; }
}

export function storeRithmicCreds(
  system: string, username: string, password: string, location = '',
): void {
  try {
    localStorage.setItem(CREDS_STORAGE_KEY, JSON.stringify({ system, username, password, location }));
  } catch { /* storage unavailable (private mode etc) — non-fatal */ }
}

export function clearStoredRithmicCreds(): void {
  try { localStorage.removeItem(CREDS_STORAGE_KEY); } catch { /* non-fatal */ }
}
